// Force TypeScript to accept createTRPCReact instead of CreateTRPCReact
import { createTRPCReact } from '@trpc/react-query';
import { httpBatchLink } from '@trpc/client';
import { QueryClient } from '@tanstack/react-query';

// Import our local type definition instead of API direct import
import type { AppRouter } from './router-types';

// Define auth error types to match API
export enum AuthErrorType {
  MISSING_TOKEN = 'MISSING_TOKEN',
  INVALID_TOKEN = 'INVALID_TOKEN',
  EXPIRED_TOKEN = 'EXPIRED_TOKEN',
}

// Token cache — declared here so handleAuthError can clear it
let cachedToken: string | null = null;
let cachedHeaders: Record<string, string> | null = null;
let tokenExpiryTime: number = 0;
const TOKEN_REFRESH_WINDOW = 5 * 60 * 1000; // Refresh token 5 minutes before expiry
let tokenRefreshInFlight: Promise<Record<string, string>> | null = null;

/**
 * Helper function to decode a JWT payload
 * Converts base64url to standard base64 before decoding
 */
function decodeJwtPayload(token: string): any {
  try {
    // Extract the payload part (second segment)
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = parts[1];

    // Convert base64url to base64 by replacing characters and adding padding
    const base64 = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(payload.length / 4) * 4, '=');

    // Decode and parse as JSON
    return JSON.parse(atob(base64));
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

// Simple structured logger with environment awareness
const logger = {
  // Only log in development mode
  isDev: () => import.meta.env.DEV || import.meta.env.MODE === 'development',

  debug: (message: string, data?: unknown) => {
    // Temporarily disable JWT/token debug logs
    if (!logger.isDev()) return;

    // Use a more targeted regex for sensitive information
    const sensitive = /(JWT|token|authorization|bearer)/i;
    if (sensitive.test(message)) {
      return; // Skip these logs
    }

    // Strip potentially sensitive fields from data before logging
    const safeData =
      typeof data === 'object'
        ? JSON.parse(
            JSON.stringify(data, (_k, v) =>
              typeof v === 'string' && v.length > 100 ? '[redacted]' : v
            )
          )
        : data;

    console.debug(`[debug] ${message}`, safeData ?? '');
  },

  info: (message: string, data?: unknown) => {
    if (!logger.isDev()) return;
    console.info(`[info] ${message}`, data || '');
  },

  warn: (message: string, data?: unknown) => {
    console.warn(`[warn] ${message}`, data || '');
  },

  error: (message: string, data?: unknown) => {
    console.error(`[error] ${message}`, data || '');
  },
};

// Helper function to handle auth errors
export function handleAuthError(error: unknown) {
  // Check if this is a tRPC error
  if (typeof error === 'object' && error !== null && 'name' in error) {
    // See if it has an auth error cause
    if ('cause' in error) {
      const cause = error.cause as { type?: AuthErrorType } | undefined;
      const errorType = cause?.type;

      if (errorType) {
        switch (errorType) {
          case AuthErrorType.EXPIRED_TOKEN:
            logger.warn('Token expired, clearing cache and retrying...');
            // Clear stale token so the next getAuthHeaders() call fetches a fresh one,
            // then invalidate all queries so TanStack Query re-fires them
            cachedToken = null;
            cachedHeaders = null;
            tokenExpiryTime = 0;
            queryClient.invalidateQueries();
            break;

          case AuthErrorType.INVALID_TOKEN:
            logger.warn('Invalid token, redirecting to login...');
            // Clear any stored auth data and redirect
            window.localStorage.removeItem('clerk-db');
            window.location.href = '/';
            break;

          case AuthErrorType.MISSING_TOKEN:
            logger.warn('Missing authentication, redirecting to login...');
            window.location.href = '/';
            break;
        }
        return true; // Error was handled
      }
    }
  }

  return false; // Error was not handled
}

// Create tRPC client with types
// @ts-expect-error - TypeScript wants CreateTRPCReact but runtime is createTRPCReact
export const trpc = createTRPCReact<AppRouter>();

// Create Query Client for TanStack Query
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: false,
      // Add global error handler for auth errors
      onError: (error) => {
        // Try to handle auth errors
        const handled = handleAuthError(error);
        if (!handled) {
          // Log unhandled errors
          logger.error('Query error:', error);
        }
      },
    },
    mutations: {
      // Add global error handler for auth errors in mutations
      onError: (error) => {
        // Try to handle auth errors
        const handled = handleAuthError(error);
        if (!handled) {
          // Log unhandled errors
          logger.error('Mutation error:', error);
        }
      },
    },
  },
});

// Create a function to get auth headers for tRPC
async function getAuthHeaders(): Promise<Record<string, string>> {
  // Return the full cached header set (includes X-User-Role for admins) if still valid
  const now = Date.now();
  if (cachedHeaders && cachedToken && tokenExpiryTime > now + TOKEN_REFRESH_WINDOW) {
    return cachedHeaders;
  }

  // Deduplicate concurrent refresh calls — all waiters share one in-flight promise
  if (tokenRefreshInFlight) {
    return tokenRefreshInFlight;
  }

  tokenRefreshInFlight = _fetchAuthHeaders().finally(() => {
    tokenRefreshInFlight = null;
  });

  return tokenRefreshInFlight;
}

async function _fetchAuthHeaders(): Promise<Record<string, string>> {
  const now = Date.now();
  try {
    // Create a Promise that will resolve with the token
    const tokenPromise = new Promise<Record<string, string>>(
      async (resolve) => {
        // Skip if not in browser or Clerk not available
        if (typeof window === 'undefined' || !window.Clerk) {
          logger.debug('Clerk not available');
          resolve({});
          return;
        }

        // Skip if no active session
        if (!window.Clerk.session) {
          logger.debug('No active Clerk session');
          resolve({});
          return;
        }

        // This function allows for multiple attempts to get a token
        const attemptTokenRetrieval = async (
          attempt = 1,
          maxAttempts = 3
        ): Promise<string | null> => {
          try {
            logger.debug(
              `Attempting to get token (attempt ${attempt} of ${maxAttempts})`
            );
            // The template parameter is needed to include publicMetadata.role in JWT
            const token =
              (await (window.Clerk?.session?.getToken as any)?.({
                template: 'Default',
              })) || '';

            if (token && token.length > 0) {
              // Decode token to get expiry time
              try {
                // Use the helper function to properly decode the JWT payload
                const payload = decodeJwtPayload(token);
                if (!payload) {
                  throw new Error('Failed to decode token payload');
                }

                // In development mode, log detailed token info
                if (logger.isDev()) {
                  const roleInfo = {
                    hasRole: !!payload.role,
                    hasClaims: !!payload.claims,
                    hasPublicMetadata: !!(payload as any).publicMetadata,
                    roleDirectly: payload.role,
                    roleInClaims: payload.claims?.role,
                    roleInPublicMetadata: (
                      (payload as any).publicMetadata as any
                    )?.role,
                  };

                  // Try to detect the role from the JWT
                  let detectedRole;
                  if (payload.role) {
                    detectedRole = payload.role;
                  } else if (payload.claims?.role) {
                    detectedRole = payload.claims.role;
                  } else if (payload.publicMetadata?.role) {
                    detectedRole = payload.publicMetadata.role;
                  }

                  logger.debug('JWT token payload analysis:', {
                    tokenStructure: roleInfo,
                    detectedRole,
                    userHasAdmin: detectedRole === 'admin',
                    // Key payload fields that might be useful for debugging
                    sub: payload.sub,
                    exp: payload.exp,
                    azp: payload.azp,
                  });
                }

                if (payload.exp) {
                  tokenExpiryTime = payload.exp * 1000; // Convert seconds to milliseconds
                  logger.debug('Token expiry time set', {
                    expiresAt: new Date(tokenExpiryTime).toISOString(),
                    expiresIn:
                      Math.round((tokenExpiryTime - now) / 1000) + ' seconds',
                  });
                }
              } catch (decodeError) {
                logger.warn('Failed to decode token expiry', {
                  error: decodeError,
                });
                // If we can't decode, set a reasonable default expiry (15 minutes)
                tokenExpiryTime = now + 15 * 60 * 1000;
              }

              // Cache the token
              cachedToken = token;
              return token;
            }

            if (attempt < maxAttempts) {
              // Wait longer between each retry
              await new Promise((r) => setTimeout(r, attempt * 500));
              return attemptTokenRetrieval(attempt + 1, maxAttempts);
            }

            logger.warn(`Failed to get token after ${maxAttempts} attempts`);
            return null;
          } catch (error) {
            logger.error(`Error getting token (attempt ${attempt})`, { error });

            if (attempt < maxAttempts) {
              // Wait longer between each retry
              await new Promise((r) => setTimeout(r, attempt * 500));
              return attemptTokenRetrieval(attempt + 1, maxAttempts);
            }

            return null;
          }
        };

        // Try to get the token with multiple attempts
        const token = await attemptTokenRetrieval();

        if (token) {
          logger.debug('Successfully retrieved JWT token', {
            tokenLength: token.length,
          });

          // Add role info to headers if the user has admin role in publicMetadata
          const headers = { Authorization: `Bearer ${token}` };

          // Log user metadata for debugging
          if (logger.isDev()) {
            logger.debug('User metadata in auth header creation:', {
              hasUser: !!window.Clerk?.user,
              hasPublicMetadata: !!(window.Clerk?.user as any)?.publicMetadata,
              role:
                ((window.Clerk?.user as any)?.publicMetadata as any)?.role ||
                'undefined',
              metadata:
                ((window.Clerk?.user as any)?.publicMetadata as any) || {},
            });
          }

          // Add the X-User-Role header for admin users regardless of route
          // This is needed because we might call admin procedures from non-admin routes
          // For example, when we create a message in the admin dialog

          try {
            // Use the helper function to properly decode the JWT payload
            const payload = decodeJwtPayload(token);

            // Log the full payload for debugging
            logger.debug('JWT Payload:', payload);

            // Always log clerk user metadata for debugging
            if (window.Clerk?.user) {
              logger.debug('Clerk User Metadata:', {
                id: (window.Clerk.user as any).id || 'unknown',
                fullMetadata: (window.Clerk.user as any).publicMetadata,
                role:
                  ((window.Clerk.user as any).publicMetadata as any)?.role ||
                  'not set',
              });
            }

            if (payload) {
              // Extract role from token payload
              let role;
              if (payload.role) {
                role = payload.role;
              } else if (payload.claims?.role) {
                role = payload.claims.role;
              } else if ((payload as any).publicMetadata?.role) {
                role = (payload as any).publicMetadata.role;
              } else if ((window.Clerk?.user as any)?.publicMetadata?.role) {
                role = ((window.Clerk.user as any).publicMetadata as any).role;
              }

              // Use clientside role if available - this will help if JWT doesn't have updated metadata
              const clerksideRole = (
                (window.Clerk?.user as any)?.publicMetadata as any
              )?.role;
              const finalRole = role || clerksideRole;

              logger.debug('Role determination:', {
                payloadRole: role || 'not found in token',
                clientRole: clerksideRole || 'not found in Clerk user',
                finalRole: finalRole || 'no role found anywhere',
                allPaths: {
                  directRole: payload.role,
                  claimsRole: payload.claims?.role,
                  metadataRole: (payload as any).publicMetadata?.role,
                  clerkRole: (
                    (window.Clerk?.user as any)?.publicMetadata as any
                  )?.role,
                },
              });

              // Always add the role header if found in either token or client
              if (finalRole === 'admin') {
                logger.debug('Adding admin role to headers', {
                  hasAdminRole: true,
                  roleValue: finalRole,
                  roleSource: role ? 'token' : 'clerk client',
                });
                (headers as Record<string, string>)['X-User-Role'] = 'admin';
              }
            }
          } catch (error) {
            logger.warn('Error extracting role from token', { error });

            // Fallback to window.Clerk if token parsing fails
            if ((window.Clerk?.user as any)?.publicMetadata?.role === 'admin') {
              logger.debug('Adding admin role to headers from Clerk user');
              (headers as Record<string, string>)['X-User-Role'] = 'admin';
            }
          }

          // Cache the full header set so the fast path returns the same headers
          // (including X-User-Role for admins) on subsequent calls
          cachedHeaders = headers;
          resolve(headers);
        } else {
          logger.error('Failed to get a valid token after all attempts');
          resolve({});
        }
      }
    );

    // Add a timeout to prevent hanging
    const timeoutPromise = new Promise<Record<string, string>>((resolve) => {
      setTimeout(() => {
        logger.warn('Token retrieval timed out after 5 seconds');
        resolve({});
      }, 5000);
    });

    return Promise.race([tokenPromise, timeoutPromise]);
  } catch (error) {
    logger.error('Unexpected error in getAuthHeaders:', error);
    return {};
  }
}

// Import API configuration
import { apiConfig } from '../lib/api-config';

// Create the tRPC client
export const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: apiConfig.trpcUrl,
      headers: getAuthHeaders,
    }),
  ],
});
